"use client";

import { useMutation } from "@apollo/client";
import { ExplorePersonRowV2 } from "components/ui/v2/explore/bundle/ExplorePersonRowV2";
import { ModalButtons } from "components/ui/v2";
import { gqlVendor } from "gql";
import { INotifyVendorUpdateSeniorityInput } from "gql/Vendor/mutations";
import useTranslation from "next-translate/useTranslation";
import toast from "react-hot-toast";
import { EUserSeniority, IUser } from "types/User.types";
import { userSeniorityDescription } from "utils/string";
import type { FormV2Props } from "../form-v2-props";

export const SENIORITY_DETAILS_FORM_ID = "seniority-details-form";

export interface SeniorityDetailsModalContentV2Props extends FormV2Props {
  users: IUser[];
  seniority: EUserSeniority;
  hideFooter?: boolean;
  formId?: string;
}

export function SeniorityDetailsModalContentV2({
  users,
  seniority,
  onClose,
  hideHeader = true,
  hideFooter = false,
  formId = SENIORITY_DETAILS_FORM_ID,
}: SeniorityDetailsModalContentV2Props) {
  const { lang, t } = useTranslation("common");

  const [notifyVendorUpdateSeniority, { loading }] = useMutation<
    { notifyVendorUpdateSeniority: boolean },
    { notifyVendorUpdateSeniorityInput: INotifyVendorUpdateSeniorityInput }
  >(gqlVendor.mutations.NOTIFY_UPDATE_SENIORITY, {
    onCompleted: () => {
      toast.success(t("Líderes notificados correctamente"));
    },
    onError: () => {
      toast.error(t("Error al notificar a los líderes"));
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    notifyVendorUpdateSeniority({
      variables: {
        notifyVendorUpdateSeniorityInput: {
          seniority,
        },
      },
    });
  };

  return (
    <form
      id={formId}
      onSubmit={handleSubmit}
      className="flex h-full flex-col"
    >
      {!hideHeader && (
        <h3 className="mb-4 text-[13px] font-semibold text-slate-900 dark:text-zinc-50">
          {userSeniorityDescription(seniority, lang)}
        </h3>
      )}
      <div className="max-h-[50vh] flex-1 space-y-3 overflow-y-auto">
        {users?.map((user) => (
          <div
            className="rounded-lg border border-slate-200/70 p-3 dark:border-zinc-800"
            key={user.id}
          >
            <p className="mb-2 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
              {t("Colaborador")}
            </p>
            <ExplorePersonRowV2
              user={user}
              secondaryLine={user.email}
              className="px-0"
            />
            {!!user.userLeaders.length && (
              <div className="mt-3 border-t border-slate-200/70 pt-3 dark:border-zinc-800">
                <p className="mb-2 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                  {t("Líderes")}
                </p>
                <div className="flex flex-col gap-1">
                  {user.userLeaders.map((userLeader) => (
                    <ExplorePersonRowV2
                      key={userLeader.id}
                      user={userLeader}
                      secondaryLine={userLeader.email}
                      className="px-0"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {!hideFooter ? (
        <ModalButtons
          loading={loading}
          onCancel={onClose}
          cancelLabel={t("Cerrar")}
          submitLabel={t("Solicitar actualización a líderes")}
        />
      ) : null}
    </form>
  );
}
